import { border } from "@smartsheet/design-tokens";
import { color } from "@smartsheet/design-tokens-react";
import { RequiredThemeKeys, themed } from "@smartsheet/sds-theme-internal";
import * as colors from "../../../constants/colors";
import { InputFieldBaseProps } from "../../../util/inputFieldBaseProps";
import { FocusMode } from "../../focusMode";

export const FI_VAR_PREFIX = "--sds--form-input--";

export const formInputTheme = themed({
    backgroundColor: {
        rest: {
            "lodestar-light": "transparent",
            "ursa-light": color("color--background--surfaces--work"),
        },
        readonly: {
            "lodestar-light": color("color--background--neutral--weaker"),
            "ursa-light": color("color--background--surfaces--work"),
        },
        error: {
            "lodestar-light": "transparent",
            "ursa-light": color("color--background--surfaces--work"),
        },
        underlined: "transparent",
    },
    borderColor: {
        error: color("color--border--error--default"),
        // We have a disconnect between Lodestar and Ursa here. In Lodestar, the focus color is used
        // when the input is focused EVEN in the error state. This is becaus the border is the only
        // indication of focus or error. If it didn't get the focus treatment, you couldn't tell if
        // it was focused in the error state. In Ursa, however, the focus indicator is a ring around
        // the input so the border AND focus ring can show.
        errorFocus: {
            // no equivalent semantic token for this — same as the Button blue colors
            "lodestar-light": colors.focus,
            "ursa-light": color("color--border--error--default"),
        },
        focus: {
            "lodestar-light": colors.focus,
            "ursa-light": color("color--border--primary--default"),
        },
        hover: {
            "lodestar-light": color("color--border--neutral--default"),
            "ursa-light": color("color--border--neutral--strong"),
        },
        readonly: {
            "lodestar-light": color("color--background--neutral--weaker"),
            "ursa-light": color("color--background--neutral--weakest"),
        },
        rest: {
            "lodestar-light": color("color--border--neutral--weak"),
            "ursa-light": color("color--border--neutral--default"),
        },
    },
    borderWidth: {
        rest: {
            "lodestar-light": 2,
            "ursa-light": 1,
        },
        active: {
            "lodestar-light": 2,
            "ursa-light": 2,
        },
        error: {
            "lodestar-light": 2,
            "ursa-light": 2,
        },
    },
    borderRadius: {
        "lodestar-light": border.radius10,
        "ursa-light": border.radius30,
    },
    searchInputButtonRadius: {
        "lodestar-light": border.radius10,
        "ursa-light": border.radius20,
    },
    searchInputButtonSize: {
        "32": {
            "lodestar-light": "30px",
            "ursa-light": "24px",
        },
        "40": {
            "lodestar-light": "30px",
            "ursa-light": "32px",
        },
    },
    padding: {
        // these are used if there is no contentBefore/contentAfter
        default: {
            rest: {
                // 12px - 1px for the border.
                "lodestar-light": 10,
                // 8px - 1px for the border.
                "ursa-light": 7,
            },
            active: {
                // 12px - 2px for the thicker active border.
                "lodestar-light": 10,
                // 8px - 2px for the border.
                "ursa-light": 6,
            },
            error: {
                // 12px - 2px for the thicker error border.
                "lodestar-light": 10,
                // 8px - 2px for the border.
                "ursa-light": 6,
            },
        },
        // used for the gap between the input and the contentBefore/contentAfter
        betweenContent: `var(${FI_VAR_PREFIX}padding--between--content, 8px)`,
        // This is used on the gap between the left bounding border and the contentLeft, if present.
        left: {
            rest: {
                // 8px - 1px for the border.
                "lodestar-light": `var(${FI_VAR_PREFIX}content-before--padding--left, 10px)`,
                "ursa-light": `var(${FI_VAR_PREFIX}content-before--padding--left, 7px)`,
            },
            active: {
                //error and active states have the same padding
                // 8px - 2px for the thicker error border.
                "lodestar-light": `var(${FI_VAR_PREFIX}content-before--padding--left, 10px)`,
                "ursa-light": `calc(var(${FI_VAR_PREFIX}content-before--padding--left, 7px) - 1px)`,
            },
        },
        // This is used on the right side of the inputs as the gap between
        // the border and the contentRight's bounding box, if present.
        right: {
            rest: {
                "lodestar-light": `var(${FI_VAR_PREFIX}content-after--padding--right, 6px)`,
                "ursa-light": `var(${FI_VAR_PREFIX}content-after--padding--right, 3px)`,
            },
            active: {
                //error and active states have the same padding
                // 4 - 2px for the thicker error border.
                "lodestar-light": `var(${FI_VAR_PREFIX}content-after--padding--right, 6px)`,
                "ursa-light": `calc(var(${FI_VAR_PREFIX}content-after--padding--right, 3px) - 1px)`,
            },
            contentAfter: {
                "lodestar-light": `var(${FI_VAR_PREFIX}content-after--padding--right, 10px)`,
                "ursa-light": `var(${FI_VAR_PREFIX}content-after--padding--right, 7px)`,
            },
        },
        top: {
            "32": {
                rest: {
                    "lodestar-light": 4.5,
                    "ursa-light": 6,
                },
                active: {
                    "lodestar-light": 4.5,
                    "ursa-light": 5,
                },
                error: {
                    "lodestar-light": 4.5,
                    "ursa-light": 5,
                },
            },
            "40": {
                rest: {
                    "lodestar-light": 8.5,
                    "ursa-light": 10,
                },
                active: {
                    "lodestar-light": 8.5,
                    "ursa-light": 9,
                },
                error: {
                    "lodestar-light": 8.5,
                    "ursa-light": 9,
                },
            },
        },
        bottom: {
            "32": {
                rest: {
                    "lodestar-light": 4.5,
                    "ursa-light": 6,
                },
                active: {
                    "lodestar-light": 4.5,
                    "ursa-light": 5,
                },
                error: {
                    "lodestar-light": 4.5,
                    "ursa-light": 5,
                },
            },
            "40": {
                rest: {
                    "lodestar-light": 8.5,
                    "ursa-light": 10,
                },
                active: {
                    "lodestar-light": 8.5,
                    "ursa-light": 9,
                },
                error: {
                    "lodestar-light": 8.5,
                    "ursa-light": 9,
                },
            },
        },
    },
    textColor: {
        rest: color("color--text--neutral--strong"),
        readonly: color("color--text--neutral--weaker"),
    },
    placeholderColor: {
        rest: color("color--text--neutral--weaker"),
        hover: {
            "lodestar-light": color("color--text--neutral--default"),
            "ursa-light": color("color--text--neutral--weaker"),
        },
    },
    placeholderTop: {
        "32": {
            "lodestar-light": "7px",
            "ursa-light": "7px",
        },
        "40": {
            "lodestar-light": "11px",
            "ursa-light": "11px",
        },
    },
    fontSize: {
        box: {
            // Inherit is needed here to override some UA styles which for some reason set the
            "ursa-light": "14px",
            // font-size to sub-px values like 13.333px. Chrome, why?
            "lodestar-light": "inherit",
        },
        underline: "15px",
    },
    fontWeight: {
        "lodestar-light": 500,
        "ursa-light": 400,
    },
    lineHeight: 18,
    focusRing: {
        offset: {
            "lodestar-light": 2,
            "ursa-light": 1,
        },
        enabled: {
            "lodestar-light": true,
            "ursa-light": true,
        },
    },
    height: {
        "32": {
            "lodestar-light": 32,
            "ursa-light": 32,
        },
        "40": {
            "lodestar-light": 40,
            "ursa-light": 40,
        },
    },
});

export interface TextInputRootBoxProps extends InputFieldBaseProps {
    isDisabled: boolean;
    isFocused: boolean;
    isHovered: boolean;
    themeMode: RequiredThemeKeys;
    validationState: "error" | undefined;
    focusMode?: FocusMode;
    hasContentAfter?: boolean;
    variant?: "underline" | "box";
}

export function getBorderColor({
    validationState,
    isFocused,
    themeMode,
    focusMode,
    isDisabled,
    isHovered,
}: TextInputRootBoxProps): string {
    const isError = validationState === "error";
    const isKeyboardFocused = focusMode === "keyboard";

    if (isFocused && isError) {
        return formInputTheme.borderColor.errorFocus(themeMode);
    } else if (isDisabled) {
        return formInputTheme.borderColor.readonly(themeMode);
    } else if (isFocused && !isKeyboardFocused) {
        return formInputTheme.borderColor.focus(themeMode);
    } else if (isFocused && isKeyboardFocused) {
        return formInputTheme.borderColor.rest(themeMode);
    } else if (isError) {
        return formInputTheme.borderColor.error(themeMode);
    } else if (isHovered) {
        return formInputTheme.borderColor.hover(themeMode);
    } else {
        return formInputTheme.borderColor.rest(themeMode);
    }
}

export function getBackgroundColor({ isDisabled, themeMode }: TextInputRootBoxProps): string {
    if (isDisabled) {
        return formInputTheme.backgroundColor.readonly(themeMode);
    } else {
        return formInputTheme.backgroundColor.rest(themeMode);
    }
}

export function getBorderWidth({ themeMode, validationState, isFocused, focusMode }: TextInputRootBoxProps): number {
    if (validationState === "error") {
        return formInputTheme.borderWidth.error(themeMode);
    } else if (isFocused && focusMode !== "keyboard") {
        return formInputTheme.borderWidth.active(themeMode);
    } else {
        return formInputTheme.borderWidth.rest(themeMode);
    }
}

export function getStylingState({
    validationState,
    isFocused,
    focusMode,
}: Pick<TextInputRootBoxProps, "validationState" | "isFocused" | "focusMode">) {
    if (validationState === "error") {
        return `error`;
    } else if (isFocused && focusMode !== "keyboard") {
        return `active`;
    } else {
        return `rest`;
    }
}
